import type { JSX } from 'react';
import { Controller, FieldValues, Path, PathValue, RegisterOptions } from 'react-hook-form';

import { FormFieldWrapperProps } from '@/widgets/ServiceFormTemplate';

import { MaskedTextInput, MaskedTextInputProps } from './MaskedTextInput';
import { TextInput, TextInputProps } from './TextInput';

type Props<T extends FieldValues> = Omit<MaskedTextInputProps, 'defaultValue'> &
  FormFieldWrapperProps<T> & {
    defaultValue?: PathValue<T, Path<T>>;
    valueType?: 'number' | 'integer' | 'string';
    transform?: 'uppercase' | 'lowercase';
    rules?: RegisterOptions<T, Path<T>>;
  };

export function FormTextInput<T extends FieldValues = FieldValues>({
  name,
  control,
  errorMessage,
  onChange,
  defaultValue,
  valueType,
  transform,
  rules,
  ...restProps
}: Props<T>): JSX.Element {
  const applyTransform = (value: string): string => {
    if (transform === 'uppercase') {
      return value.toUpperCase();
    }
    if (transform === 'lowercase') {
      return value.toLowerCase();
    }
    return value;
  };

  const convertValue = (value: string): string | number => {
    const transformedValue = applyTransform(value);

    if (valueType === 'number' || valueType === 'integer') {
      const trimmedValue = transformedValue.trim();
      if (trimmedValue === '') {
        return trimmedValue;
      }
      const numValue = Number(trimmedValue);
      return isNaN(numValue) ? transformedValue : numValue;
    }
    return transformedValue;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState: { error: fieldError } }): JSX.Element => {
        const safeValue = typeof field.value === 'string' || typeof field.value === 'number' ? String(field.value) : '';

        return restProps.mask ? (
          <MaskedTextInput
            {...field}
            value={safeValue}
            errorMessage={fieldError?.message || errorMessage}
            {...restProps}
            onChange={(event) => {
              field.onChange(applyTransform(event.target.value));
              onChange?.(event);
            }}
          />
        ) : (
          <TextInput
            {...field}
            value={safeValue}
            errorMessage={fieldError?.message || errorMessage}
            {...(restProps as TextInputProps)}
            onChange={(event) => {
              field.onChange(convertValue(event.target.value));
              onChange?.(event);
            }}
          />
        );
      }}
    />
  );
}
